import { IRegistry } from '../../../Assembler/dist/registry/iface/iRegistry.js';
import type { IData } from '../../../Assembler/dist/registry/iface/iData.js';
import type { IClient } from '../../../Assembler/dist/registry/iface/iClient.js';
import type { IView } from '../../../Assembler/dist/registry/iface/iView.js';
import type { IAction } from '../../../Assembler/dist/registry/iface/iAction.js';
import type { IDataReloadable } from '../../../Assembler/dist/registry/iface/iDataReloadable.js';
import type { IDataSource } from '../../../Assembler/dist/registry/iface/iDataSource.js';
import { ScenarioClient } from './dynamicClient/scenarioClient.js';
import { ScenarioTableClient } from './dynamicClient/scenarioTableClient.js';
import { AppSiteFoldersClient } from './dynamicClient/appSiteFoldersClient.js';
import { TimeClient } from './dynamicClient/timeClient.js';
import { CounterClient } from './dynamicClient/counterClient.js';
import { Logger } from '../../../Arshu/dist/logger/logger.js';

/**
 * Registers the CLIENT HANDLER processors — the client-tier handlers that consume a server-delivered raw data
 * island and filter/render it without a server round-trip.
 *
 * Design:
 * Client handlers are registered SEPARATELY from the server registrar on purpose. Previously they were
 * registered by RegisterServer, which also registers the filesystem-backed data providers — so any host
 * wanting client handlers had to take a dependency on ConfigFile and file IO. That made the client tier
 * impossible to bundle for the browser. This registrar registers client handlers ONLY, and the handlers it
 * registers depend solely on the pure ScenariosFilter/AppSiteFoldersFilter/TimeFilter/CounterFilter, so the
 * whole graph reachable from here is free of file IO and bundles into the browser untouched.
 *
 * The browser registers RegisterView + RegisterClient and nothing else; server hosts register this alongside
 * RegisterBase/RegisterView/RegisterServer so server behavior is unchanged.
 */
export class RegisterClient extends IRegistry {
    _registeringClientHandler: boolean = false;

    /**
     * Registers the client-handler processors into the supplied registry map. These consume the raw set
     * produced by an IClientData preparer and filter/render it client-side; they run identically in the
     * browser bundle and in the console --apiclient/--ociclient simulation.
     */
    registerClient(clientHandlerList: Map<string, IClient>): void {
        try {
            if (this._registeringClientHandler === false) {
                this._registeringClientHandler = true;
                let key = '';

                key = 'SCENARIOCLIENT';
                clientHandlerList.set(key, new ScenarioClient());

                key = 'SCENARIOTABLECLIENT';
                clientHandlerList.set(key, new ScenarioTableClient());

                key = 'APPSITEFOLDERSCLIENT';
                clientHandlerList.set(key, new AppSiteFoldersClient());

                key = 'TIMECLIENT';
                clientHandlerList.set(key, new TimeClient());

                key = 'COUNTERCLIENT';
                clientHandlerList.set(key, new CounterClient());

                this._registeringClientHandler = false;
            }
        } catch (ex) {
            Logger.error((ex as Error).message, 'RegistryClient');
        }
    }

    /**
     * No view handlers — this registrar registers client handlers only.
     */
    registerView(dynamicViewList: Map<string, IView>): void {
        void dynamicViewList;
    }

    /**
     * No data handlers — this registrar registers client handlers only.
     */
    registerData(dynamicDataList: Map<string, IData>): void {
        void dynamicDataList;
    }

    /**
     * No action handlers — this registrar registers client handlers only.
     */
    registerAction(dynamicActionList: Map<string, IAction>): void {
        void dynamicActionList;
    }

    /**
     * No app-data-backed runtime services — this registrar registers client handlers only.
     */
    registerReloadable(appDataReloadables: Map<string, IDataReloadable[]>): void {
        void appDataReloadables;
    }

    /**
     * No capture-source resolvers — this registrar registers client handlers only.
     */
    registerDataSource(dataSources: Map<string, IDataSource>): void {
        void dataSources;
    }
}
